import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import CustomerHeader from "../../components/CustomerHeader";
import CustomerFooter from "../../components/CustomerFooter";
// The transfer page's stylesheet, shared deliberately: this is the same flow entered from the other end, and a
// second copy of the same card, field, button and alert rules is a second thing to keep in step.
import "./PackageTransferPage.css";

/**
 * STOP A PACKAGE YOU DID NOT MEAN TO GIVE AWAY (user, 2026-10-09).
 *
 * PUBLIC, like the transfer itself, because the person who needs this is usually a guest and often in a hurry.
 *
 * ── THE FACTORS ARE THE OLD ONES ────────────────────────────────────────────────────────────────
 *
 * The transfer took the sender's credentials away on purpose: the account link is cleared, their phone is cleared
 * and the NUMBER ROTATED. So this asks for what they held BEFORE the move — their email, the last 4 digits of
 * their own phone, and the number that was retired. All three survive in the purchase's `ownershipHistory`, which
 * is what makes a sender who no longer holds anything still able to prove who they are.
 *
 * ── WHAT IT DOES, AND WHAT IT REFUSES TO DO ─────────────────────────────────────────────────────
 *
 * It FREEZES: the package cannot be booked, claimed or passed on, and the new holder is told. It does NOT
 * reverse the transfer and does not decide who owns it — the sender might be the one in the wrong, and a review
 * has not happened. Support can undo a freeze; that is the phase before final testing.
 *
 * The page says all of that plainly, because somebody arriving here is already worried and an over-promise
 * ("we will return it to you") would be the worst thing to read.
 */

const API_BASE = String(process.env.REACT_APP_API_BASE_URL || "").replace(
  /\/+$/,
  "",
);

const groupDigits = (value) =>
  String(value || "")
    .replace(/\D/g, "")
    .slice(0, 16)
    .replace(/(\d{4})(?=\d)/g, "$1 ");

const PackageFreezePage = () => {
  const { purchaseId } = useParams();

  const [email, setEmail] = useState("");
  const [phoneLast4, setPhoneLast4] = useState("");
  const [oldNumber, setOldNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [frozen, setFrozen] = useState(false);

  const freeze = async (event) => {
    event.preventDefault();
    setError("");

    if (!email.trim()) {
      setError("Enter the email address the package was bought with.");
      return;
    }
    if (oldNumber.replace(/\D/g, "").length !== 16) {
      setError(
        "Enter the 16-digit number from the email you were sent when the package was given away.",
      );
      return;
    }

    setBusy(true);
    try {
      const response = await fetch(
        `${API_BASE}/api/packages/purchase/${encodeURIComponent(purchaseId)}/freeze`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            email: email.trim(),
            phoneLast4: phoneLast4.replace(/\D/g, ""),
            oldNumber: oldNumber.replace(/\D/g, ""),
          }),
        },
      );
      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        setError(data.message || "We could not freeze this package.");
        return;
      }

      setFrozen(true);
    } catch (err) {
      setError("We could not reach the server. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="ptp-page">
      <CustomerHeader />

      <main className="ptp-main">
        <span className="ptp-eyebrow">Didn&apos;t send it?</span>
        <h1 className="ptp-title">Stop this package</h1>
        <p className="ptp-lede">
          If you did not give this package away, you can stop it here — straight away, without waiting for
          us. It cannot be used or passed on while we look into it.
        </p>

        <div className="ptp-card">
          {error && (
            <div className="ptp-alert ptp-alert--error" role="alert">
              {error}
            </div>
          )}

          {frozen ? (
            <div>
              <div className="ptp-done-mark" aria-hidden="true">
                ✓
              </div>
              <h2 className="ptp-title" style={{ textAlign: "center", fontSize: "21px" }}>
                The package is stopped
              </h2>
              <p className="ptp-done-text">
                Nobody can use it now, and the person holding it has been told. Please quote{" "}
                <strong>{email.trim()}</strong> when you contact us — it is how we find the package.
              </p>
              <div className="ptp-alert ptp-alert--success" role="status">
                <strong>What happens next:</strong> we review who held it and when, and then either release it
                or return it to you. You do not need to do anything else.
              </div>
              <p className="ptp-hint" style={{ textAlign: "center", marginTop: "18px" }}>
                If the review finds the transfer was genuine, the package goes back to normal and the holder
                keeps it. A freeze is not a decision about who owns it.
              </p>
            </div>
          ) : (
            <form onSubmit={freeze}>
              <p className="ptp-hint" style={{ marginTop: 0, marginBottom: "18px" }}>
                Use the details from <strong>before</strong> the package was given away — the number changed
                when it moved, so the old one is the one that identifies you.
              </p>

              <div className="ptp-field">
                <label className="ptp-label" htmlFor="pfp-email">
                  The email the package was bought with
                </label>
                <input
                  id="pfp-email"
                  className="ptp-input"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                />
              </div>

              <div className="ptp-field">
                <label className="ptp-label" htmlFor="pfp-phone">
                  Last 4 digits of your phone
                </label>
                <input
                  id="pfp-phone"
                  className="ptp-input"
                  type="tel"
                  inputMode="numeric"
                  maxLength={4}
                  value={phoneLast4}
                  onChange={(e) => setPhoneLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                  placeholder="••••"
                />
              </div>

              <div className="ptp-field">
                <label className="ptp-label" htmlFor="pfp-number">
                  The package number you were given
                </label>
                <input
                  id="pfp-number"
                  className="ptp-input ptp-input--number"
                  type="text"
                  inputMode="numeric"
                  maxLength={19}
                  value={groupDigits(oldNumber)}
                  onChange={(e) => setOldNumber(e.target.value.replace(/\D/g, "").slice(0, 16))}
                  placeholder="0000 0000 0000 0000"
                />
                <p className="ptp-hint">
                  The number on the email you received when the package was given away. It no longer works for
                  booking — that is why it can only be used to identify you here.
                </p>
              </div>

              <button type="submit" className="ptp-btn" disabled={busy}>
                {busy ? "Stopping the package…" : "Stop this package"}
              </button>
            </form>
          )}
        </div>

        <p className="ptp-hint" style={{ textAlign: "center", marginTop: "20px" }}>
          If you would rather talk to someone, email{" "}
          <a href="mailto:support@ogestyle.com" style={{ color: "#4a90c6", fontWeight: 600 }}>
            support@ogestyle.com
          </a>
          .
        </p>

        <Link className="ptp-footer-link" to="/">
          Back to OGESTYLE
        </Link>
      </main>

      <CustomerFooter />
    </div>
  );
};

export default PackageFreezePage;
